<template>
  <div>
    <div class="container-xxl dashboard dashboard-background position-relative" data-testid="shuttle-dashboard">
      <dashboard-header
        title="flexiShuttle"
        :date-selection="dateSelection"
        :workgroups="workgroups"
        :selected-workgroups="selectedWorkgroups"
        :workgroups-loading="isLoading.workgroups"
        @date-change="onDateRangeChange"
        @workgroups-change="refreshWorkgroups"
      />

      <devices-and-actions
        v-if="telematicDevices && actionsRequired"
        :telematics="telematicDevices"
        :actions-required="actionsRequired"
      />
      <div class="d-flex flex-wrap gap-3">
        <div class="ride-requests-tile">
          <ride-requests v-if="rideRequests" :value="rideRequests" />
        </div>
        <div class="riders-tile">
          <riders-graph
            v-if="hasRidersData"
            :value="numberOfRiders"
            :selected-route-ids="ridersShownRouteIds"
            :no-routes="ridersRouteIds == null && hasNoRouteGroupRoutes"
            :export-route-ids="exportRouteScope(ridersRouteIds)"
            :report-days="presetDateRange"
            :is-loading="isLoading.numberOfRiders"
            @riders-refresh="(routeIds) => getNumberOfRiders(routeIds).catch(showError)"
          />
        </div>
      </div>

      <occupancy-graph
        v-if="Object.keys(occupancyRatios.DYNAMIC)"
        :value="occupancyRatios"
        :selected-route-ids="occupancyShownRouteIds"
        :no-routes="occupancyRouteIds == null && hasNoRouteGroupRoutes"
        :export-route-ids="exportRouteScope(occupancyRouteIds)"
        :dates="{ startDay: format(startDate, 'dd.MM.yyyy'), endDay: format(endDate, 'dd.MM.yyyy') }"
        :is-loading="isLoading.occupancyRatios"
        @occupancyRefresh="(routeIds) => getOccupancyRatios(routeIds).catch(showError)"
        @tab-changed="handleOccupancyTabChange"
      />

      <!-- With route groups picked, these wait for their routes: created earlier, they would start
           unfiltered requests that can come back after, and overwrite, the filtered ones. -->
      <ridership-dashboard
        v-if="routePresetReady"
        :preset-route-ids="selectedWorkgroupRouteIds"
        :preset-date-range="presetDateRange"
      />

      <otp-dashboard-module
        v-if="routePresetReady"
        class="mt-3"
        :preset-route-ids="selectedWorkgroupRouteIds"
        :preset-date-range="presetDateRange"
      />
    </div>
  </div>
</template>

<script>
import { addDays, format, isValid, lastDayOfMonth, parseISO, subDays } from 'date-fns';
import { defaults } from '@/helpers/appDefaults';
import { dateStrToInt, getDateRangeByType } from '@/helpers/dateTime';
import { cloneObject } from '../../../../helpers/utility';
import isWorkgroupInRange from '../../helpers/workgroupPeriod';
import workgroupService from '../../services/workgroup';
import dashboardService from '../../services/dashboard';
import routeListService from '../../services/routeList';
import dashboardHeader from '../../components/dashboard/DashboardHeader.vue';
import devicesAndActions from '../../components/dashboard/devicesAndActions.vue';
import rideRequests from '../../components/dashboard/rideRequests.vue';
import ridersGraph from '../../components/dashboard/ridersGraph.vue';
import occupancyGraph from '../../components/dashboard/occupancyGraph.vue';
import otpDashboardModule from '../../components/dashboard/otp/otpDashboardModule.vue';
import ridershipDashboard from '../../components/dashboard/ridership/RidershipDashboardModule.vue';
import toaster from '../../mixins/toaster';
import { DASHBOARD_US_DEFAULT_DATE } from './dashboardUsQuery';

export default {
  name: 'FlexiShuttleDashboardUs',
  components: {
    dashboardHeader,
    devicesAndActions,
    rideRequests,
    ridersGraph,
    occupancyGraph,
    otpDashboardModule,
    ridershipDashboard,
  },
  mixins: [toaster],
  data() {
    return {
      workgroups: [],
      // workgroups is empty both before the response and when the range has none; only the latter can prune.
      workgroupsLoaded: false,
      // Date preset type (thisMonth, lastSevenDays, ..., customDate) shown in the date filter.
      selectedDate: DASHBOARD_US_DEFAULT_DATE,
      startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      endDate: lastDayOfMonth(new Date()),
      options: {
        campusId: undefined,
        fromDate: format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), defaults.date),
        routeId: undefined,
        toDate: format(lastDayOfMonth(new Date()), defaults.date),
        workgroupIds: undefined,
        workingTypes: 'DYNAMIC,FIXED',
        workgroupType: 'SHUTTLE',
      },
      telematicDevices: undefined,
      actionsRequired: undefined,
      occupancyRatios: {
        DYNAMIC: { data: [], routes: [] },
        FIXED: { data: [], routes: [] },
      },
      numberOfRiders: {
        DYNAMIC: {},
        FIXED: {},
        routes: [],
      },
      rideRequests: undefined,
      isLoading: {
        occupancyRatios: false,
        numberOfRiders: false,
        workgroups: false,
      },
      selectedWorkgroups: undefined,
      // Routes of the route groups picked in workgroup-search, preselected in the charts' own route
      // filters (which list every route). null while no route group is picked, so those charts show every route.
      selectedWorkgroupRouteIds: null,
      // Routes the riders / occupancy data was last fetched for when the user picked them in that graph;
      // [] once they cleared the filter there, which means every route, as in Ridership and OTP. null when
      // the dashboard fetched it (on load, and on every filter change): the data then follows the picked
      // route groups and the graph shows their routes. These graphs don't fetch their own data, so the
      // dashboard tells them what's on screen.
      ridersRouteIds: null,
      occupancyRouteIds: null,
      // Latest occupancy-ratios-all-routes request; older responses are dropped.
      allRoutesRequestId: 0,
      // Whether that request has settled, i.e. selectedWorkgroupRouteIds is final (or failed to load).
      routePresetSettled: false,
      // Local state for occupancy working types - not shared with other dashboard cards
      occupancyWorkingTypes: 'DYNAMIC,FIXED',
    };
  },
  computed: {
    startDateFormatted() {
      return format(this.startDate, defaults.date);
    },
    endDateFormatted() {
      return format(this.endDate, defaults.date);
    },
    // What the date filter shows; the range also labels a custom selection.
    dateSelection() {
      return { type: this.selectedDate, fromDate: this.startDateFormatted, toDate: this.endDateFormatted };
    },
    // What the riders / occupancy route filters show: the user's own pick, an empty list once they cleared
    // it, and the route groups' routes while it's null (until they touch it, and after a refetch).
    ridersShownRouteIds() {
      return this.ridersRouteIds ?? this.selectedWorkgroupRouteIds ?? [];
    },
    occupancyShownRouteIds() {
      return this.occupancyRouteIds ?? this.selectedWorkgroupRouteIds ?? [];
    },
    // Route groups are picked but have no routes (or their routes failed to load): untouched, the riders and
    // occupancy filters say so rather than "All Routes", and their data is empty.
    hasNoRouteGroupRoutes() {
      return Array.isArray(this.selectedWorkgroupRouteIds) && !this.selectedWorkgroupRouteIds.length;
    },
    // The response may lack a working type (no dynamic routes, say), which leaves that side undefined.
    hasRidersData() {
      const { DYNAMIC, FIXED } = this.numberOfRiders;
      return [DYNAMIC, FIXED].some((stats) => stats && Object.keys(stats).length);
    },
    // Ridership and OTP are created once their route preset is known: right away without picked route
    // groups, otherwise when the route lookup has settled (on failure they show every route).
    routePresetReady() {
      return !this.options.workgroupIds?.length || this.routePresetSettled;
    },
    // The header's date, preset on the charts that have their own date filter. Same shape as
    // date-range-filter emits ({ type, fromDate, toDate, startDay, endDay }).
    presetDateRange() {
      return {
        ...this.dateSelection,
        startDay: dateStrToInt(this.startDateFormatted),
        endDay: dateStrToInt(this.endDateFormatted),
      };
    },
  },

  // The filters are restored here rather than in mounted(): the charts are created while this screen
  // renders, before its mounted() runs, and they read their presets (dates, routes) when created. Restoring
  // later started them on the defaults, fetched, then refetched — and a late first response could win.
  created() {
    // The route guard (dashboardUsQuery) has completed the query by now. Presets are recomputed from
    // today; only customDate carries its own range. parseISO reads 'yyyy-MM-dd' as local midnight —
    // new Date() would read UTC midnight, shift a day in UTC-negative timezones, and setQuery() would
    // then push the shifted date and remount this screen in a loop.
    const { date, fromDate, toDate, workgroupIds } = this.$route.query;
    if (date) {
      const range = getDateRangeByType(date, { fromDate, toDate });
      const start = range.fromDate && parseISO(range.fromDate);
      const end = range.toDate && parseISO(range.toDate);
      if (start && end && isValid(start) && isValid(end)) {
        this.selectedDate = date;
        this.setDateRange(start, end);
      }
    }
    // The picked route groups ride in the URL too: every filter change pushes the query, and the shell's
    // fullPath-keyed router-view rebuilds this screen, which would otherwise drop them.
    const ids = `${workgroupIds || ''}`
      .split(',')
      .map(Number)
      .filter((id) => Number.isInteger(id) && id > 0);
    if (ids.length) this.setWorkgroupIds(ids);
  },
  async mounted() {
    // A URL this screen corrects (an invalid route-group id dropped, say) rebuilds it; the new screen fetches.
    if (await this.setQuery()) return;
    // Route groups depend only on the date, and a date change rebuilds this screen, so they're fetched here
    // once rather than in initData (which also runs on route-group picks).
    this.getWorkgroups();
    this.initData();
  },
  // Every filter change rebuilds this screen; a route-group response landing afterwards mustn't push this
  // instance's query over the new one's.
  beforeDestroy() {
    this.disposed = true;
  },
  methods: {
    format,
    // Each card's request stands on its own: one failing (ride-requests rejecting a long route-group list,
    // say) used to skip the rest of the batch and the route lookup. A failure shows one toast.
    async initData() {
      const results = [
        ...(await Promise.allSettled([this.getTelematicDevices(), this.getActionsRequired(), this.getRideRequests()])),
        ...(await Promise.allSettled([
          this.getOccupancyRatios(),
          this.getOccupancyRatiosAllRoutes(),
          this.getNumberOfRiders(),
        ])),
      ];
      const failure = results.find(({ status }) => status === 'rejected');
      if (failure) this.showError(failure.reason);
    },
    // Backend errors carry error.error.message; a network failure (a 414, a reset connection) has only
    // error.message, and reading the former used to throw inside the error handler.
    showError(error) {
      this.getToaster('error', this.$t('flexigo.common.error'), error?.error?.message || error?.message || '');
    },
    // The Excel export's route scope for the riders or occupancy chart, the same as its data's: the user's own pick
    // ([] once cleared, i.e. every route), else the picked route groups' routes. The export endpoint takes route ids
    // but not workgroupIds, so null blocks it when those routes are unknown or there are none, rather than export the
    // whole company.
    exportRouteScope(pickedRouteIds) {
      if (pickedRouteIds) return pickedRouteIds;
      if (!this.options.workgroupIds?.length) return [];
      return this.selectedWorkgroupRouteIds?.length ? this.selectedWorkgroupRouteIds : null;
    },
    // Applies a filter change: puts the filters in the URL, and fetches unless that rebuilt the screen. The shell
    // keys its router-view by the URL, so a new URL brings a new screen that fetches every card on mount; fetching
    // here as well sent every request twice (VSER-15189). A navigation that didn't go through leaves this screen
    // in place, and it fetches after all.
    async applyFilters() {
      if (!(await this.setQuery()) && !this.disposed) this.initData();
    },
    // Puts the filters in the URL. Resolves to whether the navigation went through, i.e. the screen is rebuilt.
    setQuery() {
      const query = {
        lang: this.$i18n.locale,
        date: this.selectedDate,
        fromDate: format(this.startDate, defaults.date),
        toDate: format(this.endDate, defaults.date),
      };
      if (this.options.workgroupIds?.length) query.workgroupIds = this.options.workgroupIds.join(',');

      // Pushing a query identical to the current one is an aborted navigation at best.
      // Skipping it keeps the fullPath-keyed router-view from remounting this screen —
      // which is what made every card fetch its data twice on entry (VSER-14075).
      // Both key sets count, so dropping workgroupIds is a change too.
      // Compared as text, as vue-router does: a repeated key arrives as an array (['5', '5']) and equals '5,5'.
      const current = this.$route.query;
      const keys = new Set([...Object.keys(query), ...Object.keys(current)]);
      const asText = (value) => (value == null ? undefined : String(value));
      if ([...keys].every((key) => asText(current[key]) === asText(query[key]))) return Promise.resolve(false);

      return this.$router.push({ query }).then(
        () => true,
        () => false,
      );
    },
    async getWorkgroups() {
      this.isLoading.workgroups = true;
      try {
        // Frozen: tens of thousands of read-only rows, so Vue skips making each one reactive.
        // Only the header range's route groups: a company's full history can be ~21k rows. The screen is
        // rebuilt on every date change, so this runs again with the new range.
        // A day's margin on each side: the backend compares whole days in its own timezone, which can leave out
        // an instance that overlaps the range here. workgroup-search and keepWorkgroupsInRange trim to the range.
        const workgroups = await workgroupService.getWorkgroups('SHUTTLE', undefined, undefined, undefined, {
          fromDate: format(subDays(this.startDate, 1), defaults.date),
          toDate: format(addDays(this.endDate, 1), defaults.date),
        });
        if (this.disposed) return;
        this.workgroups = Object.freeze(workgroups || []);
        this.workgroupsLoaded = true;
        // A selection restored from the URL (a bookmark, a preset recomputed for a later month) can hold route
        // groups this range doesn't list; they'd stay picked with no row to untick them.
        const range = { fromDate: this.startDateFormatted, toDate: this.endDateFormatted };
        if (this.keepWorkgroupsInRange(range)) this.applyFilters();
      } catch (error) {
        this.showError(error);
      } finally {
        this.isLoading.workgroups = false;
      }
    },
    async getTelematicDevices() {
      const response = await dashboardService.getTelematicDevices(this.options);
      this.telematicDevices = response.telematicDevices;
    },
    async getActionsRequired() {
      this.actionsRequired = await dashboardService.getActionsRequired();
    },
    async getRideRequests() {
      const payload = this.options;
      [this.rideRequests] = await dashboardService.getRideRequests(payload);
    },
    handleOccupancyTabChange(selectedTab) {
      // Determine new workingTypes based on selected tab
      let newWorkingTypes;
      if (selectedTab === 'Fixed') {
        newWorkingTypes = 'FIXED';
      } else if (selectedTab === 'Dynamic') {
        newWorkingTypes = 'DYNAMIC';
      } else {
        newWorkingTypes = 'DYNAMIC,FIXED';
      }

      // Only fetch if workingTypes actually changed
      if (this.occupancyWorkingTypes !== newWorkingTypes) {
        this.occupancyWorkingTypes = newWorkingTypes;
        // Refresh occupancy data with new workingTypes (undefined = all routes)
        this.getOccupancyRatios(undefined).catch(this.showError);
      }
    },
    async getOccupancyRatios(routeIds) {
      const payload = cloneObject(this.options);
      // Apply occupancy-specific workingTypes filter
      payload.workingTypes = this.occupancyWorkingTypes;
      // Use `routeIds` (array) instead of `routeId` (single integer) so the
      // service can serialize it as repeated query params (collectionFormat:
      // multi). The backend's `routeId` param only accepts a single integer
      // and returns 400 when given a comma-separated string.
      const ids = routeIds == null ? [] : [].concat(routeIds);
      this.occupancyRouteIds = routeIds == null ? null : ids;
      // The user's own pick (or clearing it) replaces the route groups, as in Ridership and OTP: the filter
      // lists every route, and one outside the route groups would otherwise match no trips.
      if (routeIds != null) delete payload.workgroupIds;
      if (ids.length) {
        payload.routeIds = ids;
        delete payload.routeId;
      }

      this.isLoading.occupancyRatios = true;
      try {
        const response = await dashboardService.getOccupancyRatios(payload);
        this.occupancyRatios.DYNAMIC.data = response.find((item) => item.type === 'DYNAMIC')?.stats;
        this.occupancyRatios.FIXED.data = response.find((item) => item.type === 'FIXED')?.stats;
      } finally {
        this.isLoading.occupancyRatios = false;
      }
    },
    async getOccupancyRatiosAllRoutes() {
      // Filters can change while this is in flight (another route-group pick), so the request works on
      // a copy, and only the latest request may write the route lists and the preset.
      this.allRoutesRequestId += 1;
      const requestId = this.allRoutesRequestId;
      const payload = cloneObject(this.options);
      try {
        await this.applyRouteLists(requestId, payload);
      } catch (error) {
        // The picked route groups' routes are unknown, so the charts must not fall back to every route:
        // an empty preset makes Ridership and OTP show no data.
        if (requestId === this.allRoutesRequestId && payload.workgroupIds?.length) this.selectedWorkgroupRouteIds = [];
        throw error;
      } finally {
        if (requestId === this.allRoutesRequestId) this.routePresetSettled = true;
      }
    },
    async applyRouteLists(requestId, payload) {
      // The route filters list every route, as Ridership's and OTP's do; the picked route groups only decide
      // which of them start selected, so their routes are a second request.
      const { workgroupIds, ...withoutRouteGroups } = payload;
      const [response, routeGroupRoutes, activeRoutes] = await Promise.all([
        dashboardService.getOccupancyRatiosAllRoutes(withoutRouteGroups),
        workgroupIds?.length ? dashboardService.getOccupancyRatiosAllRoutes(payload) : null,
        // Cross-reference with /routes/list-routes to get route status, since
        // occupancy-ratios-all-routes only returns { id, name, type } without
        // any active/passive indicator. Filter out passive (non-ACTIVE) FIXED
        // routes so the dropdown only shows routes the user can actually select.
        // Dynamic routes are not filtered — passive dynamic routes are still
        // listed per product requirement.
        routeListService.getAllShuttleRoutes({
          workgroupType: payload.workgroupType,
          campusId: payload.campusId,
        }),
      ]);
      if (requestId !== this.allRoutesRequestId) return;
      const activeRouteIds = new Set(
        (activeRoutes || []).filter((item) => item?.routeStatus === 'ACTIVE').map((item) => item.id),
      );
      this.occupancyRatios.DYNAMIC.routes = response.filter((item) => item.type === 'DYNAMIC');
      this.occupancyRatios.FIXED.routes = response.filter(
        (item) => item.type === 'FIXED' && activeRouteIds.has(item.id),
      );
      this.numberOfRiders.routes = [...this.occupancyRatios.DYNAMIC.routes, ...this.occupancyRatios.FIXED.routes];
      // Narrowed to the picked route groups (and the date range), so its ids are the routes to preselect.
      // Not filtered by ACTIVE like the lists above: passive routes still carry data.
      this.selectedWorkgroupRouteIds = routeGroupRoutes ? [...new Set(routeGroupRoutes.map(({ id }) => id))] : null;
    },
    async getNumberOfRiders(routeIds) {
      const payload = cloneObject(this.options);
      const ids = routeIds == null ? [] : [].concat(routeIds);
      this.ridersRouteIds = routeIds == null ? null : ids;
      payload.routeId = ids.length ? ids.join(',') : undefined;
      // As in getOccupancyRatios: the user's own pick replaces the route groups.
      if (routeIds != null) delete payload.workgroupIds;

      this.isLoading.numberOfRiders = true;
      try {
        const response = await dashboardService.getNumberOfRiders(payload);
        this.numberOfRiders.DYNAMIC = response.find((item) => item.type === 'DYNAMIC');
        this.numberOfRiders.FIXED = response.find((item) => item.type === 'FIXED');
      } finally {
        this.isLoading.numberOfRiders = false;
      }
    },
    async refreshWorkgroups(selectedWorkgroups) {
      if (selectedWorkgroups.length) {
        this.options.workgroupIds = selectedWorkgroups.map((item) => item.id);
        this.selectedWorkgroups = selectedWorkgroups;
      } else {
        this.options.workgroupIds = undefined;
        this.selectedWorkgroups = undefined;
      }
      this.applyFilters();
    },
    // From the URL only the ids are known; workgroup-search matches its rows by id, so { id } is enough.
    setWorkgroupIds(ids) {
      this.options.workgroupIds = ids;
      this.selectedWorkgroups = ids.map((id) => ({ id }));
    },

    setDateRange(startDate, endDate) {
      this.startDate = startDate;
      this.endDate = endDate;
      this.options.fromDate = this.startDateFormatted;
      this.options.toDate = this.endDateFormatted;
    },
    // fromDate/toDate are 'yyyy-MM-dd' strings from getDateRangeByType; see mounted() for why parseISO.
    onDateRangeChange({ type, fromDate, toDate }) {
      const start = parseISO(fromDate);
      const end = parseISO(toDate);
      if (!isValid(start) || !isValid(end)) return;
      // The filter also reports its initial selection; nothing to do when it's what's already shown.
      if (type === this.selectedDate && fromDate === this.startDateFormatted && toDate === this.endDateFormatted)
        return;

      this.selectedDate = type;
      this.setDateRange(start, end);
      this.keepWorkgroupsInRange({ fromDate, toDate });
      this.applyFilters();
    },
    // Picked route groups that don't run in the range (or aren't in the loaded list) drop out of the selection.
    // Kept, they'd stay listed in workgroup-search (applied route groups always are), so the list would look
    // like another range's. Returns whether the selection changed.
    keepWorkgroupsInRange(range) {
      const ids = this.options.workgroupIds;
      // Before the route groups load there's nothing to check them against.
      if (!ids?.length || !this.workgroupsLoaded) return false;
      const inRange = new Set(
        this.workgroups.filter((workgroup) => isWorkgroupInRange(workgroup, range)).map(({ id }) => id),
      );
      const kept = ids.filter((id) => inRange.has(id));
      if (kept.length === ids.length) return false;
      if (kept.length) {
        this.setWorkgroupIds(kept);
      } else {
        this.options.workgroupIds = undefined;
        this.selectedWorkgroups = undefined;
      }
      return true;
    },
  },
};
</script>

<style lang="scss" scoped>
// Ride Requests and Number of Riders share a row while both can be at least 400px wide; below that they
// wrap and each takes the full width.
// - flex-basis 0 with grow 1 : 2 splits the whole row 1:2 (the old col-4 / col-8). A 400px basis would
//   split only the space left after 400px each, which is not 1:2.
// - min-width decides the wrap: a tile is never squeezed under 400px, so the row breaks once 400 + gap
//   + 400 no longer fits. Between that and a 1:2 split Ride Requests stays at 400px and the chart takes
//   the rest.
// - min(…, 100%) lets a tile go under 400px, to the full width, when the dashboard itself is narrower.
.ride-requests-tile,
.riders-tile {
  min-width: min(400px, 100%);
}

.ride-requests-tile {
  flex: 1 1 0;
}

.riders-tile {
  flex: 2 1 0;
}
</style>
